import { capitalize } from 'lodash'
import { Box, FormHelperText, Grid, Stack, TextField, useTheme, Typography, useMediaQuery, Divider } from '@mui/material'
import { CardElement, useRecurly } from '@recurly/react-recurly'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useRum from '../../../../providers/rum/useRum'
import logger from '../../../../utils/logger'
import { StyledFormError } from '../../../auth/components/StyledFormAlert'
import StyledFormDivider from '../../../auth/components/StyledFormDivider'
import SubscriptionErrorCodes from '../../errorCodes'
import CountrySelect from './CountrySelect'
import { SubscriptionTokenType } from '../../../../graphql/generated'
import config, { environment } from '../../../../config'
import Loader from '../../../../components/Loader'

// Import styled components from a separate file to avoid circular dependencies
import { ApplePayButton, PayPalPayButton, StyledButton } from './styled'

interface RecurlyToken {
  type: string
  id: string
}

interface PaymentAuthorizedEvent {
  payment: {
    recurlyToken: RecurlyToken
    billingContact: object
    shippingContact: object
  }
}

interface Errors {
  general?: string | null
  card?: string | null
  first_name?: string | null
  last_name?: string | null
  country?: string | null
  postal_code?: string | null
  paypal?: string | null
  apple?: string | null
  cashapp?: string | null
}

interface PaymentFormProps {
  enableCashApp: boolean
  hasPendingOrSuccessfulRequest: boolean
  buttonText: string
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  formRef: React.RefObject<HTMLFormElement | null>
  errors: Errors
  setErrors: (errors: Errors) => void
  setToken: (token: RecurlyToken | null) => void
  setTokenType: (type: SubscriptionTokenType | null) => void
  planName: string
  billingCycle: string
  total: string
}

const getPlanName = (planName: string) => {
  switch (planName.toLowerCase()) {
    case 'storage':
      return 'My Bucket'
    case 'sharing':
      return 'Group Buckets'
    case 'premium':
    case 'expert':
      return 'Hosting'
    default:
      return capitalize(planName)
  }
}

export default function PaymentForm({
  enableCashApp,
  hasPendingOrSuccessfulRequest,
  buttonText,
  handleSubmit,
  formRef,
  errors,
  setErrors,
  setToken,
  setTokenType,
  planName,
  billingCycle,
  total
}: Readonly<PaymentFormProps>) {
  const theme = useTheme()
  const recurly = useRecurly()
  const { generateErrorEvent, recordError } = useRum()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [applePayInitializing, setApplePayInitializing] = useState(true)
  const isLocalhost = window.location.hostname === 'localhost'
  const location = useLocation()
  const isFreeTrial = location.pathname.includes('/subscription/free-trial')
  const isRecoveryPayment = location.pathname.includes('/subscription/recovery-payment')

  const paypal = useMemo(() => {
    return recurly.PayPal({
      display: { displayName: 'Photobucket' },
      payPalComplete: true
    })
  }, [recurly])

  useEffect(() => {
    const paypalButton = document.querySelector('#paypal-button')
    if (paypalButton) {
      paypalButton.addEventListener('click', () => {
        setErrors({})
        paypal.start()
      })
    }
    return () => {
      paypalButton?.removeEventListener('click', () => {
        paypal.start()
      })
    }
  }, [paypal, setErrors])

  paypal.on('error', function handler(err: string) {
    const errorEvent = generateErrorEvent(SubscriptionErrorCodes.RecurlyPaypalFailed, { message: err })
    recordError(errorEvent)
    setErrors({ paypal: err.toString() })
    logger.error('PayPal Error:', err)
  })

  paypal.on('cancel', function handler() {
    logger.info('PayPal checkout was canceled by the user')
    setErrors({})
  })

  paypal.on('token', function handler(recurlyToken: RecurlyToken) {
    setTokenType(SubscriptionTokenType.Paypal)
    setToken(recurlyToken)
  })

  const onPaymentAuthorized = useCallback(
    (paymentAuthorizedEvent: PaymentAuthorizedEvent) => {
      const { recurlyToken } = paymentAuthorizedEvent.payment
      setTokenType(SubscriptionTokenType.Apple)
      setToken(recurlyToken)
    },
    [setToken, setTokenType]
  )

  const applePay = useMemo(() => {
    if (!isLocalhost) {
      return recurly.ApplePay({
        country: 'US',
        currency: 'USD',
        label: `for ${getPlanName(planName || '')} ${capitalize(billingCycle || '')} Subscription`,
        total,
        recurring: true,
        callbacks: {
          onPaymentAuthorized
        }
      })
    }
    return undefined
  }, [isLocalhost, recurly, planName, billingCycle, total, onPaymentAuthorized])

  useMemo(() => {
    if (applePay && !isLocalhost) {
      applePay.on('ready', () => setApplePayInitializing(false))
      logger.info('applePayInitializing', applePayInitializing)
    }
  }, [applePay, applePayInitializing, isLocalhost])

  const handleApplePay = useCallback(() => {
    if (!isLocalhost && applePay) {
      applePay.begin()
      applePay.on('error', function handler(err: Error) {
        const errorEvent = generateErrorEvent(SubscriptionErrorCodes.RecurlyApplePayFailed, { message: err.message, error: err })
        recordError(errorEvent)
        setErrors({ ...errors, apple: err.message })
      })
    }
  }, [isLocalhost, applePay, generateErrorEvent, recordError, errors, setErrors])

  const cashApp = useMemo(() => {
    if (!recurly || !enableCashApp) return null

    return recurly.AlternativePaymentMethods({
      allowedPaymentMethods: ['cashapp'],
      blockedPaymentMethods: [],
      containerSelector: '#cash-app-container',
      amount: parseFloat(total) || 0.0,
      currency: 'USD',
      countryCode: 'US',
      locale: 'en-US',
      channel: 'Web',
      adyen: {
        publicKey: config.adyenApiKey,
        env: environment === 'production' ? 'live' : 'test',
        showPayButton: true,
        componentConfig: {}
      }
    })
  }, [recurly, enableCashApp, total])

  const attachCashappEventHandlers = useCallback(async () => {
    if (!cashApp) {
      return
    }

    cashApp.on('token', (tokenResponse: RecurlyToken) => {
      setTokenType(SubscriptionTokenType.Cashapp)
      setToken(tokenResponse)
    })

    cashApp.on('error', (err: any) => {
      if (err?.message?.includes('Customer dismissed the modal')) {
        logger.debug('Cash App modal dismissed by user.')
        return
      }

      if (err?.message?.includes('Cash App container not found')) {
        logger.debug('Cash App container not found.')
        return
      }

      if (err?.message?.includes('Error rendering CashAppPay button')) {
        logger.debug('Error rendering CashAppPay button.')
        return
      }

      const message = err.message ?? 'An error occurred with Cash App Pay.'
      const errorEvent = generateErrorEvent(SubscriptionErrorCodes.RecurlyCashAppFailed, { message, error: err })
      recordError(errorEvent)
      setErrors({ ...errors, cashapp: message })
      logger.error('Cash App Error:', err)
    })
  }, [cashApp, generateErrorEvent, recordError, errors, setErrors, setToken, setTokenType])

  useEffect(() => {
    let ignore = false
    // avoid double initialization
    if (!cashApp || ignore) {
      return () => {
        ignore = true
      }
    }
    cashApp.start()
    cashApp.on('valid', () => {
      attachCashappEventHandlers().catch((error) => {
        logger.error('Error initializing AlternativePaymentMethods:', error)
        setErrors({ ...errors, cashapp: 'Failed to initialize Cash App Pay.' })
      })
    })
    return () => {
      if (cashApp) {
        ignore = true
        try {
          // @ts-ignore
          if (cashApp.gatewayStrategy) {
            // @ts-ignore
            cashApp.gatewayStrategy.destroy()
          }
          cashApp.off('token')
          cashApp.off('error')
        } catch (error) {
          // Log the error but don't throw to prevent component unmount issues
          logger.warn('Error during Cash App cleanup:', error)
        }
      }
    }
  }, [cashApp, setErrors, attachCashappEventHandlers, errors])

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      {hasPendingOrSuccessfulRequest ? (
        <Box
          sx={{
            minHeight: isMobile ? '200px' : '600px', // Approximate height of the form content
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <Box role="status" aria-live="polite">
            {isMobile && <Divider sx={{ width: '100%', mb: 2 }} />}
            <Loader />
            <Typography variant="body1" color="text.secondary">
              Processing payment...
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          {(isFreeTrial || isRecoveryPayment) && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                Choose a Payment Method
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'blue',
                  fontSize: { xs: '0.8rem', lg: '0.9rem', xl: '1rem' }
                }}
              >
                {isRecoveryPayment
                  ? `Your first payment of $5.00 will be on ${new Date(
                      Date.now()
                    ).toLocaleDateString()} and billing will recur on a monthly basis. You can cancel anytime.`
                  : `Your first payment of $8.00 will be on ${new Date(
                      Date.now() + 14 * 24 * 60 * 60 * 1000
                    ).toLocaleDateString()} and billing will recur on monthly basis. You can cancel anytime.`}
              </Typography>
            </Box>
          )}
          {errors.general && <StyledFormError title="Error" body={errors.general} />}
          {enableCashApp ? (
            <Grid container spacing={0} gap="8px" justifyContent="center">
              <Grid item xs={12}>
                <ApplePayButton
                  disabled={hasPendingOrSuccessfulRequest}
                  type="button"
                  fullWidth
                  data-testid="apple-pay-button"
                  variant="contained"
                  onClick={handleApplePay}
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Apple_logo_white.svg/1010px-Apple_logo_white.svg.png"
                    alt="Apple Pay"
                    style={{ height: '20px' }}
                  />
                  Apple Pay
                </ApplePayButton>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" sx={{ gap: '8px' }}>
                  {hasPendingOrSuccessfulRequest ? (
                    <Box
                      id="cash-app-container"
                      data-testid="cash-app-container-button"
                      sx={{
                        width: '50%',
                        '.adyen-checkout__payment-method--selected .adyen-checkout__payment-method__header': { display: 'none' },
                        '.adyen-checkout__payment-method__details': { padding: 0, opacity: '0.25', pointerEvents: 'none' },
                        '.adyen-checkout__payment-method__details__content': { margin: 0 },
                        '.adyen-checkout__cashapp-button button': { padding: '0 !important' }
                      }}
                    />
                  ) : (
                    <Box
                      id="cash-app-container"
                      data-testid="cash-app-container-button"
                      sx={{
                        width: '50%',
                        '.adyen-checkout__payment-method--selected .adyen-checkout__payment-method__header': { display: 'none' },
                        '.adyen-checkout__payment-method__details': { padding: 0 },
                        '.adyen-checkout__payment-method__details__content': { margin: 0 },
                        '.adyen-checkout__cashapp-button button': {
                          padding: '0 !important'
                        }
                      }}
                    />
                  )}
                  <PayPalPayButton
                    fullWidth
                    type="button"
                    disabled={hasPendingOrSuccessfulRequest}
                    data-testid="paypal-button"
                    id="paypal-button"
                    style={{ borderRadius: '8px' }}
                    variant="contained"
                  >
                    Pay with{' '}
                    <img
                      src="https://content.photobucket.com/img/subscription/paypal_logo.png"
                      alt="PayPal"
                      style={{ height: '18px', marginLeft: '4px' }}
                    />
                  </PayPalPayButton>
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={0} gap={0} justifyContent="center">
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                <PayPalPayButton
                  fullWidth
                  type="button"
                  disabled={hasPendingOrSuccessfulRequest}
                  data-testid="paypal-button"
                  sx={{ borderRadius: '8px', marginRight: { xs: '0px', lg: '7px' }, marginBottom: { xs: '10px', lg: '0px' } }}
                  variant="contained"
                  id="paypal-button"
                >
                  Pay with{' '}
                  <img
                    src="https://content.photobucket.com/img/subscription/paypal_logo.png"
                    alt="PayPal"
                    style={{ height: '18px', marginLeft: '4px' }}
                  />
                </PayPalPayButton>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                <ApplePayButton
                  disabled={hasPendingOrSuccessfulRequest}
                  type="button"
                  fullWidth
                  sx={{ marginLeft: { xs: '0px', lg: '7px' } }}
                  data-testid="apple-pay-button"
                  variant="contained"
                  onClick={handleApplePay}
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Apple_logo_white.svg/1010px-Apple_logo_white.svg.png"
                    alt="Apple Pay"
                    style={{ height: '24px', marginRight: '4px' }}
                  />
                  {' Apple Pay'}
                </ApplePayButton>
              </Grid>
            </Grid>
          )}
          {errors.paypal && <StyledFormError title="PayPal Error" body={errors.paypal} />}
          {errors.apple && <StyledFormError title="Apple Pay Error" body={errors.apple} />}
          {enableCashApp && errors.cashapp && <StyledFormError title="Cash App Pay Error" body={errors.cashapp} />}
          <StyledFormDivider>PAY BY CARD</StyledFormDivider>

          <CardElement />
          {errors.card && <FormHelperText sx={{ ml: 2, color: theme.palette.error.main }}>{errors.card}</FormHelperText>}
          <TextField
            required
            fullWidth
            size="small"
            error={Boolean(errors.first_name)}
            helperText={errors.first_name}
            placeholder="First Name"
            inputProps={{ 'data-testid': 'first-name', 'data-recurly': 'first_name' }}
            sx={{
              mt: 2,
              '& fieldset': {
                borderRadius: '8px'
              }
            }}
          />
          <TextField
            required
            fullWidth
            error={Boolean(errors.last_name)}
            helperText={errors.last_name}
            size="small"
            placeholder="Last Name"
            inputProps={{ 'data-testid': 'last-name', 'data-recurly': 'last_name' }}
            sx={{
              mt: 2,
              '& fieldset': {
                borderRadius: '8px'
              }
            }}
          />
          <CountrySelect error={errors.country} />
          {errors.country && <FormHelperText sx={{ color: theme.palette.error.main }}>{errors.country}</FormHelperText>}
          <TextField
            fullWidth
            error={Boolean(errors.postal_code)}
            helperText={errors.postal_code}
            required
            size="small"
            placeholder="Zip Code"
            inputProps={{
              'data-testid': 'zip-code',
              'data-recurly': 'postal_code'
            }}
            sx={{
              mt: 2,
              '& fieldset': {
                borderRadius: '8px'
              }
            }}
          />
          <StyledButton data-testid="submit" disabled={hasPendingOrSuccessfulRequest} sx={{ mt: 2, mb: 6 }} fullWidth type="submit">
            {buttonText}
          </StyledButton>
        </>
      )}
    </form>
  )
}
